import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'app/api';
import { setSnackbar } from './snackbarSlice';

// Mint an access token for the caller. role: 'publisher' | 'viewer'.
// Returns { url, token, room, identity, canPublish, startedAt, expiresAt }.
// The first token request for a site starts the call; `expiresAt` is when the
// server ends the room for everyone (fixed lifetime, 60 min by default).
export const getLiveKitToken = createAsyncThunk(
    'livekit/getToken',
    async ({ siteId, role }, { dispatch, rejectWithValue }) => {
        try {
            const res = await API.post(
                '/livekit/token',
                { role },
                { params: { SiteId: siteId } }
            );
            return res.data;
        } catch (err) {
            dispatch(
                setSnackbar({
                    status: 'error',
                    message: 'Unable to join the live stream',
                })
            );
            return rejectWithValue(err.message);
        }
    }
);

// List active participants in the site's call (manager view / refresh).
// `silent` suppresses the error snackbar — used by the background presence poll,
// which runs on an interval and shouldn't nag the user about transient failures.
export const getLiveKitParticipants = createAsyncThunk(
    'livekit/getParticipants',
    async ({ siteId, silent = false }, { dispatch, rejectWithValue }) => {
        try {
            const res = await API.get('/livekit/participants', {
                params: { SiteId: siteId },
            });
            return res.data;
        } catch (err) {
            if (!silent) {
                dispatch(
                    setSnackbar({
                        status: 'error',
                        message: 'Unable to load participants',
                    })
                );
            }
            return rejectWithValue(err.message);
        }
    }
);

// Remove one participant (Site:Manager), then re-fetch the list.
export const removeLiveKitParticipant = createAsyncThunk(
    'livekit/removeParticipant',
    async ({ siteId, identity }, { dispatch, rejectWithValue }) => {
        try {
            const res = await API.post('/livekit/remove', null, {
                params: { SiteId: siteId, identity },
            });
            dispatch(
                setSnackbar({
                    status: 'success',
                    message: 'Participant removed',
                })
            );
            dispatch(getLiveKitParticipants({ siteId }));
            return res.data;
        } catch (err) {
            dispatch(
                setSnackbar({
                    status: 'error',
                    message: 'Failed to remove participant',
                })
            );
            return rejectWithValue(err.message);
        }
    }
);

const livekitSlice = createSlice({
    name: 'livekit',
    initialState: {
        url: null,
        token: null,
        room: null,
        identity: null,
        canPublish: false,
        // ISO timestamp for when the server ends the current call for everyone.
        expiresAt: null,
        participants: [],
        loading: false,
        error: false,
    },
    reducers: {
        clearLiveKitSession: (state) => {
            state.url = null;
            state.token = null;
            state.room = null;
            state.identity = null;
            state.canPublish = false;
            state.expiresAt = null;
            state.participants = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getLiveKitToken.pending, (state) => {
                state.loading = true;
                state.error = false;
            })
            .addCase(getLiveKitToken.fulfilled, (state, action) => {
                state.loading = false;
                state.url = action.payload.url;
                state.token = action.payload.token;
                state.room = action.payload.room;
                state.identity = action.payload.identity;
                state.canPublish = action.payload.canPublish;
                state.expiresAt = action.payload.expiresAt ?? null;
            })
            .addCase(getLiveKitToken.rejected, (state) => {
                state.loading = false;
                state.error = true;
            })
            .addCase(getLiveKitParticipants.fulfilled, (state, action) => {
                state.participants = action.payload || [];
            });
    },
});

export const { clearLiveKitSession } = livekitSlice.actions;
export default livekitSlice.reducer;
